import { Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setSession as setReduxSession, clearSession } from './store/slices/authSlice';
import { fetchAdminProfile, clearAdminProfile } from './store/slices/usersSlice';
import { RootState } from './store';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import notificationService, { ForegroundNotification } from './services/notificationService';
import Breadcrumb from './components/Breadcrumb/Breadcrumb';
import Login from './components/auth/Login';

// Tabs
import SupportTicketsTab from './components/sidenavbar/public/SupportTicketsTab';
import CustomerDetailsPage from './components/sidenavbar/Users/CustomerDetails';
import NetworkTab from './components/sidenavbar/Network/Network';
import NetworkUserDetailsPage from './components/sidenavbar/Network/NetworkUserDetails';
import ACFHome from './components/sidenavbar/Acf/ACFHome';
import ACFUserDetails from './components/sidenavbar/Acf/ACFUserDetails';

// Public Pages
import ReferralLandingPage from './components/sidenavbar/public/ReferralLandingPage';
import DeactivateUserPage from './components/sidenavbar/public/DeactivateUserPage';

// Redux
import { setHighlightedOrderId, setHighlightedMilestoneId, addNotification } from './store/slices/uiSlice';

// Privacy
import PrivacyPolicy from './components/sidenavbar/public/PrivacyPolicy';
import Support from './components/sidenavbar/public/Support';

// Skeletons
import OrdersPageSkeleton from './components/common/skeletons/OrdersPageSkeleton';
import UsersPageSkeleton from './components/common/skeletons/UsersPageSkeleton';
import ProductsPageSkeleton from './components/common/skeletons/ProductsPageSkeleton';
import EmiCalculatorSkeleton from './components/common/skeletons/EmiCalculatorSkeleton';

// Tabs (Lazy Loaded)
const OrdersTab = React.lazy(() => import('./components/sidenavbar/Orders/OrdersTab'));
const UserDetails = React.lazy(() => import('./components/sidenavbar/Users/UserDetails'));
const ProductsTab = React.lazy(() => import('./components/sidenavbar/products/ProductsTab'));
const EmiCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/Emi/EmiCalculatorTab'));
const AcfCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/Acf/AcfCalculatorTab'));
const UnitCalculatorTab = React.lazy(() => import('./components/sidenavbar/Calculators/Unit/UnitCalculatorTab'));
const FarmManagement = React.lazy(() => import('./components/sidenavbar/Farm/FarmManagement'));
const SelfBenefitsTab = React.lazy(() => import('./components/sidenavbar/SelfBenefits/SelfBenefitsTab'));
const RoleRequestsTab = React.lazy(() => import('./components/sidenavbar/RoleRequests/RoleRequestsTab'));
const OffersAchievedTab = React.lazy(() => import('./components/sidenavbar/OffersAchieved/OffersAchievedTab'));
const RoasterTab = React.lazy(() => import('./components/sidenavbar/Roaster/RoasterTab'));
const AdminListTab = React.lazy(() => import('./components/sidenavbar/Users/AdminListTab'));

interface Session {
  mobile: string;
  role: string | null;
  name?: string;
  referralCode?: string;
  lastLoginTime?: string;
  currentLoginTime?: string;
}




// Smart redirect component removed as it is no longer needed

function App() {
  const [session, setSession] = useState<Session | null>(() => {
    const saved = window.localStorage.getItem('ak_dashboard_session');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        window.localStorage.removeItem('ak_dashboard_session');
      }
    }
    return null;
  });
  const dispatch = useAppDispatch();
  const { adminProfile, adminProfileLoading, adminProfileError } = useAppSelector((state: RootState) => state.users);
  const navigate = useNavigate();
  const location = useLocation();

  // Sync session stats to Redux exactly once when session is set/changed
  useEffect(() => {
    if (session) {
      dispatch(setReduxSession({
        adminMobile: session.mobile,
        adminName: session.name || 'Admin',
        adminRole: session.role || 'Admin',
        lastLogin: session.lastLoginTime || 'First Login',
        presentLogin: session.currentLoginTime || new Date().toLocaleString(),
        referralCode: session.referralCode || '',
      }));
    }
  }, [dispatch, session]); // Only re-run if session changes

  // Each toast has its own id so they stack independently
  type Toast = ForegroundNotification & { id: string };
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const dismissToast = useCallback((id: string) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const pushToast = useCallback((payload: ForegroundNotification) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts(prev => [...prev, { ...payload, id }]);
    toastTimers.current[id] = setTimeout(() => dismissToast(id), 8000);
  }, [dismissToast]);

  // Fetch admin profile EXACTLY ONCE per session initialization
  const fetchStartedRef = useRef(false);
  useEffect(() => {
    if (session?.mobile && !adminProfile && !adminProfileLoading && !fetchStartedRef.current) {
      fetchStartedRef.current = true;
      dispatch(fetchAdminProfile(session.mobile));
    }
  }, [session?.mobile, adminProfile, adminProfileLoading, dispatch]);

  // Wire up FCM: register SW, get/save token, subscribe to admin topic,
  // and listen for foreground and background messages.
  useEffect(() => {
    if (!session?.mobile) return;

    const roles = session.role ? session.role.split(',').map((r) => r.trim()) : [];

    // Foreground listener: store in Redux, show stacked toast, navigate + highlight
    const unsubscribeMessage = notificationService.onForegroundMessage((payload) => {
      dispatch(addNotification({ title: payload.title, body: payload.body, data: payload.data }));
      pushToast(payload);
    });

    // Init FCM, save token, subscribe to admin topic
    notificationService.onUserLogin(session.mobile, roles);

    // Handle clicks on background OS notifications (posted by the service worker)
    const handleSwMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'FCM_NOTIFICATION_CLICK') return;
      const url: string = event.data.url || '/orders';
      const urlObj = new URL(url, window.location.origin);
      const highlightOrder = urlObj.searchParams.get('highlight_order');
      const highlightMilestone = urlObj.searchParams.get('highlight_milestone');
      if (highlightOrder) dispatch(setHighlightedOrderId(highlightOrder));
      if (highlightMilestone) dispatch(setHighlightedMilestoneId(highlightMilestone));
      navigate(urlObj.pathname);
    };

    navigator.serviceWorker?.addEventListener('message', handleSwMessage);

    return () => {
      unsubscribeMessage();
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.mobile]);

  // Parse highlight query-params on cold open (tab opened by SW on notification click)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlightOrder = params.get('highlight_order');
    const highlightMilestone = params.get('highlight_milestone');
    if (highlightOrder) dispatch(setHighlightedOrderId(highlightOrder));
    if (highlightMilestone) dispatch(setHighlightedMilestoneId(highlightMilestone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleLogin = useCallback((s: Session) => {
    // Determine last login (from previous session or current if new)
    const prevSessionStr = window.localStorage.getItem('ak_dashboard_session');
    let lastLoginTime = new Date().toLocaleString();

    if (prevSessionStr) {
      try {
        const prevSession = JSON.parse(prevSessionStr);
        if (prevSession.currentLoginTime) {
          lastLoginTime = prevSession.currentLoginTime;
        }
      } catch (e) { }
    }

    const newSession = {
      ...s,
      currentLoginTime: new Date().toLocaleString(),
      lastLoginTime: lastLoginTime
    };

    window.localStorage.setItem('ak_dashboard_session', JSON.stringify(newSession));
    setSession(newSession);
    dispatch(setReduxSession({
      adminMobile: newSession.mobile,
      adminName: newSession.name || 'Admin',
      adminRole: newSession.role || 'Admin',
      lastLogin: newSession.lastLoginTime || 'First Login',
      presentLogin: newSession.currentLoginTime || new Date().toLocaleString(),
      referralCode: newSession.referralCode || '',
    }));

    const userRoles = newSession.role ? newSession.role.split(',').map(r => r.trim()) : [];
    const hasAdminAccess = userRoles.some(r => ['Admin', 'Animalkart admin', 'SuperAdmin'].includes(r));

    let defaultPath = '/orders';
    if (hasAdminAccess) {
      defaultPath = '/orders';
    }

    // Navigate to origin or default
    const from = (location.state as any)?.from?.pathname;
    const targetPath = from && from !== '/login' ? from : defaultPath;

    navigate(targetPath, { replace: true });
  }, [dispatch, location.state, navigate]);

  const handleLogout = () => {
    if (session?.mobile) {
      const roles = session.role ? session.role.split(',').map((r) => r.trim()) : [];
      notificationService.onUserLogout(session.mobile, roles);
    }
    // Clear Redux state
    dispatch(clearSession());
    dispatch(clearAdminProfile());
    // Reset fetch flag for next login
    fetchStartedRef.current = false;

    // Clear ALL local storage for security and a clean state
    window.localStorage.clear();
    setSession(null);
    navigate('/login', { replace: true });
  };

  const userRoles = session?.role ? session.role.split(',').map((r: string) => r.trim().toLowerCase()) : [];
  const isAdmin = userRoles.some((r: string) => ['admin', 'animalkart admin', 'superadmin'].includes(r));



  return (
    <div className="App">
      {/* Stacked foreground notification toasts — bottom-right column */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9997,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        maxWidth: '360px',
        width: '360px',
        pointerEvents: toasts.length === 0 ? 'none' : 'auto',
      }}>
        {toasts.map((toast) => {
          const hasLink = !!(toast.data?.order_id || toast.data?.milestone_id || toast.data?.recipient_mobile);
          const handleView = () => {
            const data = toast.data || {};
            if (data.type === 'MILESTONE_ACHIEVED' && data.milestone_id) {
              dispatch(setHighlightedMilestoneId(data.milestone_id));
              navigate('/offer-settings');
            } else if (data.type === 'REFERRAL_REWARD' && data.recipient_mobile) {
              navigate(`/user-management/network/${data.recipient_mobile}`);
            } else if (data.order_id) {
              dispatch(setHighlightedOrderId(data.order_id));
              navigate('/orders');
            }
            dismissToast(toast.id);
          };
          return (
            <div key={toast.id} style={{
              backgroundColor: '#1e293b',
              color: 'white',
              padding: '14px 16px',
              borderRadius: '10px',
              boxShadow: '0 10px 25px -3px rgba(0,0,0,0.5)',
              border: '1px solid rgba(99,102,241,0.25)',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              animation: 'slideIn 0.25s ease-out',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ fontWeight: 600, fontSize: '13px', lineHeight: '1.4', flex: 1 }}>{toast.title}</span>
                <button
                  onClick={() => dismissToast(toast.id)}
                  style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0', flexShrink: 0 }}>
                  ✕
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.5', color: '#94a3b8' }}>{toast.body}</p>
              {hasLink && (
                <button
                  onClick={handleView}
                  style={{
                    marginTop: '6px',
                    alignSelf: 'flex-end',
                    background: 'none',
                    border: '1px solid rgba(99,102,241,0.4)',
                    color: '#818cf8',
                    cursor: 'pointer',
                    padding: '3px 12px',
                    borderRadius: '4px',
                    fontSize: '11px',
                  }}>
                  View →
                </button>
              )}
            </div>
          );
        })}
      </div>

      <Routes>
        <Route path="/login" element={
          session ? <Navigate to="/orders" replace /> : <Login onLogin={handleLogin} />
        } />

        {/* Persistent Layout for all authenticated dashboard views */}
        <Route element={<DashboardLayout session={session} isAdmin={isAdmin} handleLogout={handleLogout} />}>

          {/* Strictly Protected Routes */}
          <Route element={<RequireAuth session={session} isAdmin={isAdmin} adminProfile={adminProfile} adminProfileLoading={adminProfileLoading} adminProfileError={adminProfileError} handleLogout={handleLogout}><Outlet /></RequireAuth>}>

            <Route path="/orders" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <OrdersTab />
              </React.Suspense>
            } />

            <Route path="/user-management" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <UserDetails />
              </React.Suspense>
            } />

            <Route path="/admin-list" element={
              <React.Suspense fallback={<UsersPageSkeleton />}>
                <AdminListTab />
              </React.Suspense>
            } />

            <Route path="/users/customers/:mobile" element={<CustomerDetailsPage />} />

            <Route path="/products" element={
              <React.Suspense fallback={<ProductsPageSkeleton />}>
                <ProductsTab />
              </React.Suspense>
            } />

            <Route path="/user-management/network" element={<NetworkTab />} />
            <Route path="/user-management/network/:mobile" element={<NetworkUserDetailsPage />} />

            <Route path="/acf" element={<ACFHome />} />
            <Route path="/acf/details/:userId/:orderId" element={<ACFUserDetails />} />

            <Route path="/support-tickets" element={<SupportTicketsTab />} />
            <Route path="/farm-management" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <FarmManagement />
              </React.Suspense>
            } />
            <Route path="/offer-settings" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <SelfBenefitsTab />
              </React.Suspense>
            } />
            <Route path="/offers-achieved" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <OffersAchievedTab />
              </React.Suspense>
            } />
            <Route path="/role-requests" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <RoleRequestsTab />
              </React.Suspense>
            } />
            <Route path="/roaster" element={
              <React.Suspense fallback={<OrdersPageSkeleton />}>
                <RoasterTab />
              </React.Suspense>
            } />
          </Route>

          {/* Public/Hybrid Routes - Layout visible if logged in */}


          <Route path="/unit-calculator" element={
            <React.Suspense fallback={<OrdersPageSkeleton />}>
              <UnitCalculatorTab tree={true} />
            </React.Suspense>
          } />

          <Route path="/emi-calculator" element={
            <React.Suspense fallback={<EmiCalculatorSkeleton />}>
              <EmiCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/acf-calculator" element={
            <React.Suspense fallback={<EmiCalculatorSkeleton />}>
              <AcfCalculatorTab />
            </React.Suspense>
          } />

          <Route path="/referral-landing" element={<ReferralLandingPage />} />
          <Route path="/deactivate-user" element={<DeactivateUserPage />} />

          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />


        </Route>

        {/* Backward Compatibility Redirects */}
        <Route path="/dashboard/orders" element={<Navigate to="/orders" replace />} />
        <Route path="/dashboard/user-management" element={<Navigate to="/user-management" replace />} />
        <Route path="/dashboard/products" element={<Navigate to="/products" replace />} />

        <Route path="/dashboard/emi-calculator" element={<Navigate to="/emi-calculator" replace />} />
        <Route path="/dashboard/acf-calculator" element={<Navigate to="/acf-calculator" replace />} />
        <Route path="/dashboard/*" element={<Navigate to="/orders" replace />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to={session ? "/orders" : "/login"} replace />} />
        <Route path="*" element={<Navigate to={session ? "/orders" : "/login"} replace />} />
      </Routes>
    </div>
  );
}

const DashboardLayout = ({ session, isAdmin, handleLogout }: { session: Session | null, isAdmin: boolean, handleLogout: () => void }) => {
  const location = useLocation();

  // Define routes that should ALWAYS show the sidebar (protected dashboard content)
  const protectedPrefixes = [
    '/orders',
    '/user-management',
    '/users/customers',
    '/products',
    '/acf',
    '/support-tickets',
    '/farm-management',
    '/offer-settings',
    '/role-requests',
    '/offers-achieved',
    '/roaster',
    '/admin-list'
  ];

  const isProtectedPath = protectedPrefixes.some(prefix => location.pathname.startsWith(prefix)) && !location.pathname.startsWith('/acf-calculator');

  // If the user navigates from the dashboard (via sidebar), state.fromDashboard will be true
  const isFromDashboard = !!(location.state as any)?.fromDashboard;

  // Hybrid paths are public pages that CAN show the dashboard layout if accessed from within
  const hybridPrefixes = [
    '/landify',
    '/true-harvest',
    '/support',
    '/privacy-policy',
    '/deactivate-user',
    '/unit-calculator',
    '/emi-calculator',
    '/acf-calculator',
    '/referral-landing'
  ];
  const isHybridPath = hybridPrefixes.some(prefix => location.pathname.startsWith(prefix));

  // We show the dashboard layout IF:
  // 1. It's a core protected dashboard route
  // 2. OR it's a hybrid route AND we are coming from the dashboard
  const showDashboardLayout = isProtectedPath || (isHybridPath && isFromDashboard);

  // Standalone mode is for public/hybrid pages accessed directly via URL or when no dashboard context is present
  const isStandalone = !showDashboardLayout;

  if (isStandalone) {
    return (
      <main className="h-screen w-full overflow-y-auto bg-slate-100">
        <Outlet />
      </main>
    );
  }

  return (
    <Breadcrumb
      adminMobile={session?.mobile}
      adminName={session?.name}
      adminRole={session?.role || undefined}
      lastLogin={session?.lastLoginTime}
      presentLogin={session?.currentLoginTime}
      onLogout={handleLogout}
    >
      <Outlet />
    </Breadcrumb>
  );
};

// Helper for strict protection
const RequireAuth = ({ children, session, isAdmin, adminProfile, adminProfileLoading, adminProfileError, handleLogout }: { children: React.ReactNode, session: Session | null, isAdmin: boolean, adminProfile: any, adminProfileLoading: boolean, adminProfileError: string | null, handleLogout: () => void }) => {
  const location = useLocation();
  if (!session) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 600, margin: '2rem auto', padding: '1.5rem', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '0.75rem' }}>Access Restricted</h2>
        <p style={{ marginBottom: 0 }}>Only authorized Admin users can access this dashboard. Please login with an Admin mobile.</p>
        <button onClick={handleLogout} style={{ marginTop: '1rem', padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Logout</button>
      </div>
    );
  }

  // If profile is loading OR we have a session but haven't started/finished fetching the profile yet, wait.
  // We stop waiting if an error occurs so we don't hang indefinitely.
  if ((adminProfileLoading || (session && !adminProfile)) && !adminProfileError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Rolecheck
  const sessionRoles = session.role ? session.role.split(',').map((r: string) => r.trim().toLowerCase()) : [];
  const profileRoles = adminProfile?.role ? adminProfile.role.split(',').map((r: string) => r.trim().toLowerCase()) : [];
  const isSuperAdmin = sessionRoles.some((r: string) => r.includes('superadmin')) || profileRoles.some((r: string) => r.includes('superadmin'));

  if (!isSuperAdmin) {
    const restrictedPrefixes = ['/true-harvest', '/roaster', '/farm-management', '/admin-list', '/offer-settings'];
    if (restrictedPrefixes.some(prefix => location.pathname.startsWith(prefix))) {
      return <Navigate to="/orders" replace />;
    }
  }
  return <>{children}</>;
};



export default App;
